import { readonly, ref, type Ref } from "vue";

/*****************************************************************************
 * Open state and keyboard shortcuts for the command palette (nav-10): the
 * shortcuts that open it, and the ones for the actions on its active result.
 *
 * Module-scope state, like useMobileSidebarMenu: the palette itself is mounted
 * once by PrivateHeader, and its triggers (the sidebar button, Cmd/Ctrl+K and
 * '/') live elsewhere.
 *****************************************************************************/

const isOpen = ref< boolean >( false );

// The mounted palette's show function, called in the same task as the tap or key that opens it (see CommandPalette).
let opener: ( () => void ) | null = null;

/** For CommandPalette only. Returns the unregister function. */
export function registerPaletteOpener( show: () => void ): () => void {
    opener = show;
    return () => {
        if ( opener === show ) opener = null;
    };
}

export function useCommandPalette(): {
    isPaletteOpen: Readonly< Ref< boolean > >,
    openPalette: () => void,
    closePalette: () => void,
} {
    return {
        isPaletteOpen: readonly( isOpen ),
        openPalette: () => {
            isOpen.value = true;
            opener?.();
        },
        closePalette: () => { isOpen.value = false; },
    };
}

/** macOS and iOS use Cmd, everything else Ctrl; also what the shortcut hint shows. */
export function isApplePlatform(): boolean {
    if ( typeof navigator === "undefined" ) return false;
    return /Mac|iPhone|iPad|iPod/.test( navigator.platform || navigator.userAgent );
}

/** An element that takes typed text, where '/' is a character and not a shortcut. */
export function isEditableTarget( target: EventTarget | null ): boolean {
    if ( !( target instanceof HTMLElement ) ) return false;
    if ( target.isContentEditable ) return true;
    if ( target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement ) return true;
    if ( target instanceof HTMLInputElement ) {
        // Buttons, checkboxes and the like take no text.
        return ![ "button", "checkbox", "color", "file", "image", "radio", "range", "reset", "submit" ].includes( target.type );
    }
    return false;
}

export type PaletteShortcut = "toggle" | "open" | null;

/**
 * What a keydown means to the palette.
 *
 * - Cmd+K (Apple) or Ctrl+K (elsewhere) toggles it from anywhere, text fields included; the other modifier is left
 *   alone because Ctrl+K deletes to the end of the line in a macOS text field.
 * - '/' opens it, but only outside text fields and outside another dialog, where it is a character or belongs to
 *   that dialog. Shift is allowed, since '/' needs it on some layouts.
 */
export function paletteShortcutFor( event: KeyboardEvent, apple: boolean = isApplePlatform() ): PaletteShortcut {
    if ( event.defaultPrevented || event.isComposing || event.repeat ) return null;

    const primary = apple ? event.metaKey : event.ctrlKey;
    const other = apple ? event.ctrlKey : event.metaKey;
    if ( primary && !other && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k" ) {
        return "toggle";
    }

    if ( event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey ) {
        const target = event.target;
        if ( isEditableTarget( target ) ) return null;
        if ( target instanceof Element && target.closest( "dialog, [role='dialog']" ) ) return null;
        return "open";
    }

    return null;
}

/** A secondary action on the palette's active result, besides opening it. */
export type PaletteAction = "open-in-new-tab" | "add-to-watchlist";

/**
 * The action an Enter in the palette's field asks for, or null for an Enter that opens the result (plain Enter).
 *
 * - Cmd+Enter (Apple) or Ctrl+Enter (elsewhere) opens it in a new tab, as Cmd/Ctrl+click does on a link; a sector's
 *   new tab is its own page (the preview pane's "Open sector page").
 * - Alt+Enter (Option+Enter) adds a stock or ETF to the watchlist (the preview pane's "Add to watchlist").
 *
 * Shift is never part of one: a member may still be holding it from typing a symbol in capitals, so Shift+Enter opens
 * the result as it always has. So does any other mix of modifiers.
 */
export function paletteActionFor( event: KeyboardEvent, apple: boolean = isApplePlatform() ): PaletteAction | null {
    if ( event.key !== "Enter" || event.shiftKey ) return null;

    const primary = apple ? event.metaKey : event.ctrlKey;
    const other = apple ? event.ctrlKey : event.metaKey;
    if ( other ) return null;
    if ( primary && !event.altKey ) return "open-in-new-tab";
    if ( event.altKey && !primary ) return "add-to-watchlist";
    return null;
}

export interface PaletteActionKeys {
    /** The keys as the hints show them, one <kbd> each. */
    keys: string[];
    /** The aria-keyshortcuts value. */
    aria: string;
    /** As a screen reader should say them, for the field's description. */
    spoken: string;
}

/** How an action's shortcut reads on this platform: Cmd and Option on Apple platforms, Ctrl and Alt elsewhere. */
export function paletteActionKeys( action: PaletteAction, apple: boolean = isApplePlatform() ): PaletteActionKeys {
    if ( action === "open-in-new-tab" ) {
        return apple
            ? { keys: [ "⌘", "Enter" ], aria: "Meta+Enter", spoken: "Command+Enter" }
            : { keys: [ "Ctrl", "Enter" ], aria: "Control+Enter", spoken: "Control+Enter" };
    }
    return apple
        ? { keys: [ "⌥", "Enter" ], aria: "Alt+Enter", spoken: "Option+Enter" }
        : { keys: [ "Alt", "Enter" ], aria: "Alt+Enter", spoken: "Alt+Enter" };
}
